import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { acrescentarMensagem } from "@/components/ChatPanel";
import { api } from "@/lib/api";
import type { ChatMessage } from "@/lib/chat-api";
import { juntarPaginas } from "@/lib/conversas";
import type { CaixaDeConversas, ConversaUnificada, Session } from "@/lib/types";
import { useChatStream, type ChatStreamEvent, type ChatStreamStatus } from "@/lib/useChatStream";

import {
  chaveDoChatRapido,
  gravarChatRapido,
  lerChatRapido,
  reduzirJanelas,
  RascunhosQueAvisam,
  type JanelaDoChat,
} from "./chat-rapido-estado";

export const CHAVE_DA_CAIXA = ["conversas", "caixa"] as const;

/**
 * A caixa única num cache só: o selo do ícone, a lista do chat rápido e a
 * tela `/conversas` leem a MESMA consulta, e a conversa lida numa some do
 * contador na outra sem esperar o próximo ciclo.
 */
export const opcoesDaCaixa = infiniteQueryOptions({
  queryKey: CHAVE_DA_CAIXA,
  queryFn: ({ pageParam }) => api.caixaDeConversas(pageParam),
  initialPageParam: 1,
  getNextPageParam: (ultima: CaixaDeConversas) => (ultima.externas.temMais ? ultima.externas.pagina + 1 : undefined),
});

/**
 * M1 (TUT-491) — o que muda recarrega só a PRIMEIRA página, e não todas as do
 * "Carregar mais" (cada uma é uma ida à Inbox).
 */
export function atualizarPrimeiraPagina(queryClient: QueryClient): Promise<void> {
  return api
    .caixaDeConversas(1)
    .then((nova) => {
      queryClient.setQueryData<InfiniteData<CaixaDeConversas, number>>(CHAVE_DA_CAIXA, (atual) =>
        atual ? { ...atual, pages: [nova, ...atual.pages.slice(1)] } : { pages: [nova], pageParams: [1] },
      );
    })
    .catch(() => undefined);
}

type Ouvinte = (evento: ChatStreamEvent) => void;

interface ValorDoChatRapido {
  session: Session;
  /** A caixa (todas as páginas carregadas, juntas), ou `null` antes de chegar. */
  caixa: CaixaDeConversas | null;
  carregando: boolean;
  falhou: boolean;
  tentarDeNovo: () => void;
  totalNaoLidas: number;
  janelas: JanelaDoChat[];
  abrir: (conversa: ConversaUnificada) => void;
  fechar: (id: string) => void;
  alternar: (id: string) => void;
  fecharTodas: () => void;
  /** A conversa como a caixa a vê agora, ou o último retrato dela. */
  conversaDa: (id: string) => ConversaUnificada | null;
  rascunhos: RascunhosQueAvisam;
  atualizarCaixa: () => void;
  /** No celular, o chat é a folha de tela cheia. */
  folhaAberta: boolean;
  setFolhaAberta: (aberta: boolean) => void;
  status: ChatStreamStatus;
  assinar: (ouvinte: Ouvinte) => () => void;
}

const Contexto = createContext<ValorDoChatRapido | null>(null);

/** `null` fora do shell e para quem está sem grupo (TUT-544). */
export function useChatRapido(): ValorDoChatRapido | null {
  return useContext(Contexto);
}

/**
 * TUT-590 — UMA conexão SSE por aba: o canal mora no shell e quem precisa
 * dele (a `/conversas`) assina aqui, em vez de abrir o seu. Sem o shell (ou
 * sem grupo), não chega evento e o status é "closed".
 */
export function useCanalDoChat(aoEvento: Ouvinte): { status: ChatStreamStatus } {
  const chat = useContext(Contexto);
  const aoEventoRef = useRef(aoEvento);
  aoEventoRef.current = aoEvento;
  const assinar = chat?.assinar;
  useEffect(() => assinar?.((evento) => aoEventoRef.current(evento)), [assinar]);
  return { status: chat?.status ?? "closed" };
}

/**
 * TUT-590 — o estado do chat rápido, montado UMA vez no `AppShell` para
 * sobreviver à navegação (C3): as janelas abertas, os rascunhos, a caixa com
 * o contador de não lidas (60 s, a cadência do sino) e o canal de tempo real.
 */
export function ChatRapidoProvider({ session, children }: { session: Session; children: ReactNode }) {
  // TUT-544 — sem grupo, nada de conversas: nem consulta, nem canal.
  if (session.semGrupo) return <>{children}</>;
  return <ChatRapidoAtivo session={session}>{children}</ChatRapidoAtivo>;
}

function ChatRapidoAtivo({ session, children }: { session: Session; children: ReactNode }) {
  const queryClient = useQueryClient();
  const chave = chaveDoChatRapido(session.tenant.id, session.user.id);
  const [inicial] = useState(() => lerChatRapido(chave));
  const [janelas, despachar] = useReducer(reduzirJanelas, inicial.janelas);
  const [rascunhos] = useState(() => new RascunhosQueAvisam(inicial.rascunhos));
  const [folhaAberta, setFolhaAberta] = useState(false);
  const janelasRef = useRef(janelas);
  janelasRef.current = janelas;
  const ouvintes = useRef(new Set<Ouvinte>());
  const retratos = useRef(new Map<string, ConversaUnificada>());
  const relogio = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // O que sobrevive ao recarregar: as janelas a cada mudança, o rascunho a
  // cada tecla (o `Map` avisa).
  useEffect(() => gravarChatRapido(chave, janelas, rascunhos), [chave, janelas, rascunhos]);
  useEffect(() => {
    rascunhos.aoMudar = () => gravarChatRapido(chave, janelasRef.current, rascunhos);
    return () => {
      rascunhos.aoMudar = null;
    };
  }, [chave, rascunhos]);

  const consulta = useInfiniteQuery(opcoesDaCaixa);
  const atualizarCaixa = useCallback(
    (espera = 300) => {
      clearTimeout(relogio.current);
      relogio.current = setTimeout(() => void atualizarPrimeiraPagina(queryClient), espera);
    },
    [queryClient],
  );
  useEffect(() => {
    const ciclo = setInterval(() => atualizarCaixa(0), 60_000);
    return () => {
      clearInterval(ciclo);
      clearTimeout(relogio.current);
    };
  }, [atualizarCaixa]);

  // O tempo real: a mensagem de uma janela aberta entra direto (conferindo o
  // id, TUT-340); a das outras só marca o cache como velho, sem ir buscar
  // (quem a mostra na `/conversas` acrescenta pelo próprio ouvinte). A caixa
  // recarrega sempre — é ela que dá o contador.
  const { status } = useChatStream(false, (evento) => {
    if (evento.type === "chat.message") {
      const dado = evento.data as { conversationId?: string } & Partial<ChatMessage>;
      if (dado?.conversationId) {
        const chaveDasMensagens = ["chat", "messages", false, dado.conversationId];
        const visivel = janelasRef.current.some((j) => !j.minimizada && j.id === `chat:${dado.conversationId}`);
        if (visivel && dado.id) {
          queryClient.setQueryData<{ items: ChatMessage[]; total: number }>(chaveDasMensagens, (atual) =>
            acrescentarMensagem(atual, dado as ChatMessage),
          );
        } else {
          void queryClient.invalidateQueries({ queryKey: chaveDasMensagens, refetchType: "none" });
        }
        atualizarCaixa();
      }
    }
    for (const ouvir of ouvintes.current) ouvir(evento);
  });
  // A reconexão traz o que se perdeu enquanto o canal estava fora.
  const statusAnterior = useRef(status);
  useEffect(() => {
    if (statusAnterior.current !== "open" && status === "open") {
      atualizarCaixa(0);
      void queryClient.invalidateQueries({ queryKey: ["chat", "messages", false] });
    }
    statusAnterior.current = status;
  }, [status, queryClient, atualizarCaixa]);

  const assinar = useCallback((ouvinte: Ouvinte) => {
    ouvintes.current.add(ouvinte);
    return () => {
      ouvintes.current.delete(ouvinte);
    };
  }, []);

  const caixa = useMemo(() => juntarPaginas(consulta.data?.pages ?? []), [consulta.data]);
  const valor = useMemo<ValorDoChatRapido>(() => {
    const porId = new Map((caixa?.itens ?? []).map((c) => [c.id, c]));
    for (const [id, conversa] of porId) if (retratos.current.has(id)) retratos.current.set(id, conversa);
    return {
      session,
      caixa,
      carregando: consulta.isLoading,
      falhou: consulta.isError && !caixa,
      tentarDeNovo: () => void consulta.refetch(),
      totalNaoLidas: (caixa?.itens ?? []).reduce((soma, c) => soma + c.naoLidas, 0),
      janelas,
      abrir: (conversa) => {
        retratos.current.set(conversa.id, conversa);
        despachar({ tipo: "abrir", id: conversa.id });
      },
      fechar: (id) => despachar({ tipo: "fechar", id }),
      alternar: (id) => despachar({ tipo: "alternar", id }),
      fecharTodas: () => despachar({ tipo: "fecharTodas" }),
      // A conversa que saiu da primeira página (o atendimento é paginado)
      // segue aberta com o retrato de quando foi escolhida.
      conversaDa: (id) => porId.get(id) ?? retratos.current.get(id) ?? null,
      rascunhos,
      atualizarCaixa: () => atualizarCaixa(),
      folhaAberta,
      setFolhaAberta,
      status,
      assinar,
    };
  }, [session, caixa, consulta, janelas, rascunhos, atualizarCaixa, folhaAberta, status, assinar]);

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}
